/*
 * Rechenteil der Fernwartung über Mesh (RemoteSessionDialog, RemoteSessionPanel):
 * Auswahl der Ziele, Rumpf zum Öffnen, Link zum Technician Mesh Client und
 * je Regel die passende Aktion. Ohne Preact, damit `npm run test` es direkt
 * prüft.
 *
 * Die Abbildung auf Loopback-Adressen (localEndpoints) rechnet der Mesh
 * Client hvacloud-fw genauso (hvp-tools, src/fw-helfer). Ändert sich eine
 * Seite, muss die andere mit. Einen anderen Weg als den Mesh Client gibt es
 * nicht, auch kein installiertes Tailscale (Entscheidung 04.11.2026).
 */

export interface Rule {
  listenPort: number;
  targetHost: string;
  targetPort: number;
  protocol: string;
  serviceKey: string;
}

export interface Service {
  key: string;
  port: number;
  description: string;
  requiresWrite: boolean;
}

export interface TargetSuggestion {
  host: string;
  deviceId: string | null;
  name: string | null;
  sources: string[];
  vendor: string | null;
  interface: string | null;
  suggestedServices: string[];
  lastSeenAt: string | null;
}

export type Mode = 'read' | 'write';

/** Was der Mesh Client meldet: wo eine Regel auf dem Techniker-Rechner liegt. */
export interface HelperPort {
  listenPort: number;
  host: string;
  port: number;
  error: string | null;
}

/** Ein Ziel im Dialog: aus der Geräteliste oder von Hand eingetragen. */
export interface Selection {
  host: string;
  deviceId: string | null;
  label: string;
  detail: string;
  manual: boolean;
  checked: boolean;
  services: string[];
}

/** Dienste, die im gewählten Modus wählbar sind. */
export function usableServices(services: Service[], mode: Mode): Service[] {
  return services.filter((s) => mode === 'write' || !s.requiresWrite);
}

/** IPv4 in Punktschreibweise ohne führende Nullen, wie der Server sie annimmt. */
export function isIPv4(text: string): boolean {
  const parts = text.trim().split('.');
  return parts.length === 4 && parts.every((p) => /^(0|[1-9]\d{0,2})$/.test(p) && Number(p) <= 255);
}

const SOURCE_TEXT: Record<string, string> = { device: 'angelegt', scan: 'gefunden' };

/**
 * Startauswahl aus der Geräteliste. Ein einziges Gerät ist gleich
 * angehakt: an den meisten Panels hängt genau ein Regler.
 */
export function initialSelections(targets: TargetSuggestion[]): Selection[] {
  return targets.map((t) => ({
    host: t.host,
    deviceId: t.deviceId,
    label: t.name ?? t.host,
    detail: [
      t.name ? t.host : null,
      t.sources.map((s) => SOURCE_TEXT[s] ?? s).join(' und '),
      t.interface ? `an ${t.interface}` : null,
      t.vendor && t.vendor !== t.name ? t.vendor.replace(/[,\s]+$/, '') : null,
    ].filter(Boolean).join(' · '),
    manual: false,
    checked: targets.length === 1,
    services: t.suggestedServices.length > 0 ? [...t.suggestedServices] : ['http'],
  }));
}

/** Leeres Ziel für "Weitere Adresse". */
export function manualSelection(): Selection {
  return { host: '', deviceId: null, label: '', detail: 'von Hand', manual: true, checked: true, services: ['http'] };
}

export interface OpenBody {
  reason: string;
  mode: Mode;
  targets: { host: string; services: string[]; deviceId?: string }[];
}

/**
 * Rumpf für POST …/remote-sessions. Schreibende Dienste fallen im
 * Lesemodus heraus, auch wenn sie vorher angehakt waren; der Server ließe
 * sie ohnehin weg, aber dann stünde im Protokoll ein Wunsch, den niemand
 * sehen konnte.
 */
export function buildOpenBody(reason: string, mode: Mode, selections: Selection[], services: Service[]): OpenBody {
  const allowed = new Set(usableServices(services, mode).map((s) => s.key));
  const targets = selections
    .filter((s) => s.checked && isIPv4(s.host))
    .map((s) => {
      const chosen = s.services.filter((k) => allowed.has(k));
      const t: { host: string; services: string[]; deviceId?: string } = { host: s.host.trim(), services: chosen };
      if (s.deviceId) t.deviceId = s.deviceId;
      return t;
    })
    .filter((t) => t.services.length > 0);
  return { reason: reason.trim(), mode, targets };
}

/** Was den Knopf "Öffnen" sperrt, als Satz; null heißt frei. */
export function openBlocker(reason: string, selections: Selection[]): string | null {
  if (reason.trim() === '') return 'Bitte einen Grund angeben.';
  const bad = selections.find((s) => s.checked && s.manual && s.host.trim() !== '' && !isIPv4(s.host));
  if (bad) return `${bad.host.trim()} ist keine IPv4-Adresse.`;
  return null;
}

/**
 * Link für den Mesh Client: Instanz, Sitzung und ein Einmal-Ticket (60 s, genau
 * einmal einlösbar), damit sich der Techniker nicht ein zweites Mal
 * anmeldet (Entscheidung 04.11.2026). Nie der Beitrittsschlüssel selbst.
 */
export function helperLink(instanceHost: string, sessionId: string, ticket: string): string {
  return `hvacloud-fw://verbinden?instanz=${encodeURIComponent(instanceHost)}&sitzung=${encodeURIComponent(sessionId)}`
    + `&ticket=${encodeURIComponent(ticket)}`;
}

export interface Endpoint {
  host: string;
  port: number;
}

/**
 * Wo der Mesh Client jede Regel anbietet: je Zielgerät eine eigene
 * Loopback-Adresse (erstes 127.0.0.1, dann 127.0.0.2 …), Port gleich
 * Zielport. So liegt SCOPE auf 127.0.0.1:4242 wie vor Ort. Ist ein Port am
 * Rechner belegt, weicht der Client aus und meldet den tatsächlichen.
 */
export function localEndpoints(rules: Rule[]): Map<number, Endpoint> {
  const hosts: string[] = [];
  const out = new Map<number, Endpoint>();
  for (const r of rules) {
    let i = hosts.indexOf(r.targetHost);
    if (i < 0) {
      hosts.push(r.targetHost);
      i = hosts.length - 1;
    }
    out.set(r.listenPort, { host: `127.0.0.${i + 1}`, port: r.targetPort });
  }
  return out;
}

export type RuleAction =
  | { kind: 'web'; url: string }
  | { kind: 'scope'; host: string; port: number; apiPort: number | null }
  | { kind: 'copy'; text: string };

const WEB: Record<string, 'http' | 'https'> = {
  http: 'http', 'http-alt': 'http', https: 'https', 'https-alt': 'https',
};

/** Aktion je Regel, auf dem Rechner des Technikers. */
export function ruleAction(rule: Rule, rules: Rule[], helperPorts: HelperPort[] = []): RuleAction | null {
  // Was der Mesh Client meldet, gilt vor der Rechnung: dort steht auch ein
  // Ausweichport, wenn der Zielport am Rechner belegt war.
  const reported = new Map(helperPorts.filter((p) => !p.error).map((p) => [p.listenPort, { host: p.host, port: p.port }]));
  const local = localEndpoints(rules);
  const at = (r: Rule): Endpoint | null => reported.get(r.listenPort) ?? local.get(r.listenPort) ?? null;
  const ep = at(rule);
  if (!ep) return null;
  const scheme = WEB[rule.serviceKey];
  if (scheme) {
    const dflt = (scheme === 'http' && ep.port === 80) || (scheme === 'https' && ep.port === 443);
    return { kind: 'web', url: `${scheme}://${ep.host}${dflt ? '' : `:${ep.port}`}/` };
  }
  if (rule.serviceKey === 'climatix-scope') {
    // Die Device-API des S400 ist dessen Weboberfläche auf Port 80.
    const api = rules.find((r) => r.targetHost === rule.targetHost && r.serviceKey === 'http');
    const apiEp = api ? at(api) : null;
    return { kind: 'scope', host: ep.host, port: ep.port, apiPort: apiEp?.port ?? null };
  }
  return { kind: 'copy', text: `${ep.host}:${ep.port}` };
}

/** Regeln je Zielgerät, in der Reihenfolge der Freigabe. */
export function rulesByTarget(rules: Rule[]): { host: string; rules: Rule[] }[] {
  const out: { host: string; rules: Rule[] }[] = [];
  for (const r of rules) {
    let g = out.find((x) => x.host === r.targetHost);
    if (!g) {
      g = { host: r.targetHost, rules: [] };
      out.push(g);
    }
    g.rules.push(r);
  }
  return out;
}

const SERVICE_LABEL: Record<string, string> = {
  modbus: 'Modbus TCP',
  codesys: 'CODESYS',
  'climatix-scope': 'Climatix SCOPE',
  http: 'Weboberfläche',
  https: 'Weboberfläche (TLS)',
  'http-alt': 'Weboberfläche 8080',
  'https-alt': 'Weboberfläche 8443 (TLS)',
};

/** Lesbarer Name eines Dienstes. */
export function serviceLabel(key: string): string {
  return SERVICE_LABEL[key] ?? key;
}

/** Wie lange nach "Verbinden" ein eingelöstes Ticket ausbleiben darf, bevor die Seite nachfragt. */
export const CLIENT_START_GRACE_MS = 10_000;

/**
 * Ob "Technician Mesh Client herunterladen" erscheint. Der Browser darf
 * nicht fragen, ob für hvacloud-fw:// ein Programm eingetragen ist; zwei
 * Hinweise ersetzen das: dieser Browser hat schon einmal einen verbundenen
 * Client gesehen, und nach einem Klick auf "Verbinden" wurde das Ticket
 * binnen CLIENT_START_GRACE_MS eingelöst.
 */
export function showDownload(seenBefore: boolean, clickedAt: number | null, started: boolean, now: number): 'offer' | 'missing' | null {
  if (started) return null;
  if (clickedAt !== null && now - clickedAt >= CLIENT_START_GRACE_MS) return 'missing';
  return seenBefore ? null : 'offer';
}

export type StepState = 'done' | 'current' | 'waiting';

/** Die drei Schritte der Sitzungsansicht. Verbunden heißt: der Mesh Client meldet sich oder Headscale sieht den Knoten. */
export function sessionSteps(status: 'requested' | 'active' | 'ended', technicianJoined: boolean): [StepState, StepState, StepState] {
  if (status === 'requested') return ['current', 'waiting', 'waiting'];
  if (status === 'active' && !technicianJoined) return ['done', 'current', 'waiting'];
  if (status === 'active') return ['done', 'done', 'current'];
  return ['done', 'done', 'done'];
}

/** Restzeit als Text, bei unter einer Stunde mit Sekunden. */
export function restText(until: string, now: number): string {
  const s = Math.max(0, Math.floor((new Date(until).getTime() - now) / 1000));
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min ${s % 60} s`;
}
